import { useQuery } from '@tanstack/react-query';
import type { DuplicateCandidate } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import { get } from '@/lib/api';
import { dateOnly, humanize } from '@/lib/utils';
import { Sheet } from '@/components/ui/sheet';
import { useFormScreen } from '@/components/ui/form-screen';
import { Alert, Badge, Button, SkeletonText } from '@/components/ui/primitives';
import { matchReasonLabel } from './RegistrationAside';

interface CandidateRecord {
  id: string;
  mrn: string;
  fullName: string;
  sex: string;
  ageLabel: string;
  dob: string | null;
  phone: string | null;
  city: string | null;
  guardianName: string | null;
}

function Line({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-border/60 py-2 last:border-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-end">{value || '—'}</dd>
    </div>
  );
}

export function DuplicateCandidateSheet({ candidate, onClose }: { candidate: DuplicateCandidate; onClose: () => void }) {
  const { leave } = useFormScreen();
  const record = useQuery({
    queryKey: ['patient', candidate.patientId],
    queryFn: () => get<CandidateRecord>(`/patients/${candidate.patientId}`),
  });
  const data = record.data;

  return (
    <Sheet
      open
      onClose={onClose}
      title={candidate.fullName}
      description={tr('opd', 'duplicate_sheet_kept')}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            {tr('opd', 'back_to_the_form')}
          </Button>
          <Button type="button" 
            onClick={() => {
              onClose();
              leave(`/patients/${candidate.patientId}`);
            }}
          >
            {tr('opd', 'open_this_record')}
          </Button>
        </>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="warning">{candidate.score}%</Badge>
          <span className="text-muted-foreground">{tr('opd', 'matched_on_x', { fields: candidate.matchedOn.map(matchReasonLabel).join(', ') })}</span>
        </div>
        {record.isLoading && <SkeletonText lines={5} />}
        {record.error ? <Alert variant="destructive">{tr('opd', 'duplicate_sheet_unavailable')}</Alert> : null}
        <dl>
          <Line label={tr('opd', 'mrn')} value={data?.mrn ?? candidate.mrn} />
          <Line label={tr('opd', 'sex')} value={data ? `${humanize(data.sex)} · ${data.ageLabel}` : null} />
          <Line label={tr('opd', 'date_of_birth')} value={dateOnly(data?.dob ?? candidate.dob)} />
          <Line label={tr('opd', 'phone')} value={data?.phone ?? candidate.phone} />
          <Line label={tr('opd', 'city')} value={data?.city} />
          <Line label={tr('opd', 'guardian_name')} value={data?.guardianName} />
        </dl>
      </div>
    </Sheet>
  );
}
